/**
 * Utility Functions Library
 * Contains common helper functions used throughout the application
 * Includes data manipulation, formatting, validation, and UI utility functions
 */

// NextUI components for UI elements
import {
  Avatar,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Image,
  Input,
  TableCell,
  Tooltip,
} from "@nextui-org/react";

// Third-party libraries for file handling and data processing
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import CryptoJS from "crypto-js";

// API controllers for data operations
import {
  getDataWithFilterUrl,
  getAlldata,
  StringLimit,
  getFilterDataWithPagination,
  UpdateOneData,
  PublishData,
  InviteUser,
} from "../controllers/APIController";

// Custom components and icons
import SearchIcon from "../Images/Search";
import { FieldList } from "./constant";
import DownloadIcon from "../Images/Icons/download.svg";

// Vector database controller for AI operations
import { getPromptData } from "../controllers/vectordbController";

// Color constants for UI components
const Colors = ["secondary", "success", "warning", "danger", "primary"];
const numColors = Colors.length;

/**
 * Renders a red asterisk to indicate required fields
 * Used in forms to mark mandatory input fields
 * 
 * @returns {JSX.Element} Red asterisk symbol
 */
export const RequiredFieldSign = () => {
  return <p style={{ color: "red" }}>*</p>;
};

/**
 * Creates a button for adding new entries
 * Provides a consistent UI pattern for creating new items
 * 
 * @param {Object} props - Component props
 * @param {string} props.name - Name of the entry type to create
 * @param {string} props.metricName - Metric name for the entry
 * @param {Function} props.onClickHandler - Function to call when button is clicked
 * @returns {JSX.Element} Create new entry button
 */
export const CreateNewEntryButton = ({ name, metricName, onClickHandler }) => {
  const clickHandler = () => {
    onClickHandler(metricName, name);
  };
  
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontSize: "16px",
      }}
      onClick={clickHandler}
      role="button"
      tabIndex={0}
      aria-label={`Create a new ${name} for ${metricName}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          clickHandler();
        }
      }}
    >
      <span>Create a New {name} </span>
      <span>+</span>
    </div>
  );
};

/**
 * Extracts unique class names from an array of objects
 * Filters out duplicate class names and returns a unique list
 * 
 * @param {Array} arr - Array of objects with attributes.ClassName property
 * @returns {Array} Array of unique class names
 */
export const getUniqueClassName = (arr) => {
  const uniqueValues = [];
  const map = new Map();
  
  // Return empty array if input is invalid
  if (arr === null || arr === undefined || arr.length === 0) {
    return uniqueValues;
  }
  
  // Extract unique class names using Map for O(1) lookup
  for (const item of arr) {
    if (!map.has(item?.attributes?.ClassName)) {
      map.set(item?.attributes?.ClassName, true);
      uniqueValues.push(item?.attributes?.ClassName);
    }
  }
  
  return uniqueValues;
};

/**
 * Checks if a given string is valid JSON
 * Attempts to parse the string and returns true if successful
 * 
 * @param {string} str - String to check for JSON validity
 * @returns {boolean} True if valid JSON, false otherwise
 */
export function isJsonString(str) {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Sets initial state for form data based on field definitions
 * Creates an object with default values for each form field
 * 
 * @param {Array} arr - Array of field definitions with type and value properties
 * @returns {Object} Initial form state object
 */
export const getInitailState = (arr) => {
  const data = {};

  arr?.forEach((item) => {
    if (item[1].type === "boolean") {
      data[item[0]] = "false";
    } else if (item[1].type === "slider") {
      data[item[0]] = item[1].value || 0;
    } else {
      data[item[0]] = "";
    }
  });
  
  return data;
};

/**
 * Applies syntax highlighting to JSON strings for display
 * Converts JSON to HTML with CSS classes for different data types
 * 
 * @param {string} json - JSON string to highlight
 * @returns {string} HTML string with syntax highlighting classes
 */
export function syntaxHighlight(json) {
  // Escape HTML characters to prevent XSS
  json = json
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  
  // Apply syntax highlighting with CSS classes
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    function (match) {
      let cls = "number";
      
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = "key";
        } else {
          cls = "string";
        }
      } else if (/true|false/.test(match)) {
        cls = "boolean";
      } else if (/null/.test(match)) {
        cls = "null";
      }
      
      return '<span class="' + cls + '">' + match + "</span>";
    }
  );
}

/**
 * Copies text to clipboard
 * Uses the Clipboard API to copy text to user's clipboard
 * 
 * @param {string} value - Text to copy to clipboard
 */
export const handleCopyClick = (value) => {
  navigator.clipboard
    .writeText(value)
    .then(() => {
      // Successfully copied
    })
    .catch((error) => {
      console.error("Failed to copy text:", error);
    });
};

/**
 * Formats date to YYYY-MM-DD format
 * Converts any date value to ISO date string format
 * 
 * @param {string|Date} value - Date value to format
 * @returns {string} Formatted date string in YYYY-MM-DD format
 */
export const DateFormator = (value) => {
  const date = new Date(value);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

/**
 * Formats date to DD-MM-YYYY format
 * Converts any date value to DD-MM-YYYY format for display
 * 
 * @param {string|Date} value - Date value to format
 * @returns {string} Formatted date string in DD-MM-YYYY format
 */
export const DateFormatorDD = (value) => {
  const date = new Date(value);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

/**
 * Converts date from DD-MM-YYYY to YYYY-MM-DD format
 * Useful for converting display dates to database format
 * 
 * @param {string} value - Date string in DD-MM-YYYY format
 * @returns {string|null} Date string in YYYY-MM-DD format or null if invalid
 */
export const ChangeDateDDToYYYY = (value) => {
  if (value === undefined || value === "null") return null;
  const [day, month, year] = value.split("-");
  return `${year}-${month}-${day}`;
};

/**
 * Fetches answered count and last updated timestamp for responses
 * Aggregates response data to get completion statistics
 * 
 * @param {Array} responseData - Array of response data objects
 * @returns {Promise<Object>} Object with answeredCount and lastUpdatedAt
 */
export async function FetchAnsweredCount(responseData) {
  let answeredCount = 0;
  let lastUpdatedAt = null;
  
  const promises = responseData.map(async (element) => {
    const id = element?.attributes?.Name;
    const responseUrl = "filters[Metric_Id][$eqi]=" + id + "&sort[0]=updatedAt:desc";
    
    return getFilterDataWithPagination("responses", responseUrl, 1, 1)
      .then((data) => {
        if (data.data.length > 0) {
          answeredCount = answeredCount + 1;
          lastUpdatedAt = data.data[0].attributes?.updatedAt;
        }
      })
      .catch((err) => {
        console.error("Error fetching response data:", err);
      });
  });

  try {
    await Promise.all(promises);
    return { answeredCount, lastUpdatedAt };
  } catch (err) {
    // Handle error silently
  }
}

/**
 * Fetches metric information for given metric names
 * Retrieves completion statistics for multiple metrics
 * 
 * @param {Array} metricNameList - List of metric names to fetch info for
 * @param {string} OrgInfo - Organization ID for filtering
 * @param {string} frameworkUrl - Framework URL filter string
 * @returns {Promise<Object>} Object with metric information and statistics
 */
export async function fetchMetricInfo(metricNameList, OrgInfo, frameworkUrl) {
  const result = {};
  
  const promisesResponse = metricNameList.map(async (name) => {
    const filteredUrl = `filters[Organization][id][$eqi]=${+OrgInfo}&filters[Category][$eqi]=${name}${frameworkUrl}`;

    return getDataWithFilterUrl("metrics", filteredUrl)
      .then(async (data) => {
        const responseData = data.data;
        let totalCount = responseData.length;
        let inCompleteCount = 0, completedCount = 0;
        let lastUpdatedAt = "";

        responseData?.map(async (item) => {
          const lastUpdatedReponse = item?.attributes?.lastUpdatedReponse || "";
          const currCount = item?.attributes?.currentCounter;
          let requiredCount = item?.attributes?.requiredCounter;

          currCount >= requiredCount ? completedCount++ : inCompleteCount++;

          if (lastUpdatedReponse !== "" && lastUpdatedAt === "") {
            lastUpdatedAt = new Date(lastUpdatedReponse);
          }
          if (
            lastUpdatedReponse !== "" &&
            new Date(lastUpdatedReponse) > new Date(lastUpdatedAt)
          ) {
            lastUpdatedAt = lastUpdatedReponse;
          }
        });
        result[name] = {
          totalCount: totalCount,
          InCompleteCount: inCompleteCount,
          CompletedCount: completedCount,
          lastUpdatedAt: lastUpdatedAt,
        };
      })
      .catch((err) => {
        console.log(err);
      });
  });

  try {
    await Promise.all(promisesResponse);
    return result;
  } catch (err) {
    console.log("An error occurred", err);
  }
}

// formatDate in like 22 April,2020
export const formatDate = (dateStr) => {
  const date = new Date(dateStr);

  const options = { day: "numeric", month: "long", year: "numeric" };

  return date.toLocaleDateString("en-GB", options);
};

export const generateAPIKey = () => {
  const length = 32;
  const characters =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let randomString = "";

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    randomString += characters[randomIndex];
  }

  return randomString;
};

export const checkAIServiceConfiguration = () => {
  const config = {
    isConfigured: false,
    serviceUrl: null,
    issues: []
  };

  // Check if environment variable is set
  if (!process.env.REACT_APP_HAYA_IP_ADDRESS) {
    config.issues.push("REACT_APP_HAYA_IP_ADDRESS environment variable is not set");
    return config;
  }

  config.serviceUrl = process.env.REACT_APP_HAYA_IP_ADDRESS;
  config.isConfigured = true;

  // Check if the URL looks valid
  try {
    const url = new URL(config.serviceUrl);
    if (!url.protocol || !url.hostname) {
      config.issues.push("Invalid service URL format");
      config.isConfigured = false;
    }
  } catch (error) {
    config.issues.push("Invalid service URL format");
    config.isConfigured = false;
  }

  return config;
};

export const testAIServiceConnection = async () => {
  const config = checkAIServiceConfiguration();
  
  if (!config.isConfigured) {
    return {
      isAvailable: false,
      error: config.issues.join(", "),
      responseTime: null
    };
  }

  try {
    const startTime = Date.now();
    const response = await fetch(`${config.serviceUrl}/health`, {
      method: 'GET',
      mode: 'cors',
      signal: AbortSignal.timeout(10000) // 10 second timeout
    });
    const responseTime = Date.now() - startTime;

    if (response.ok) {
      return {
        isAvailable: true,
        error: null,
        responseTime: responseTime
      };
    } else {
      return {
        isAvailable: false,
        error: `Service responded with status: ${response.status}`,
        responseTime: responseTime
      };
    }
  } catch (error) {
    return {
      isAvailable: false,
      error: error.message,
      responseTime: null
    };
  }
};

export const getFormulaData = async (formules, formData) => {
  // Safety check: ensure formules is an array
  if (!formules || !Array.isArray(formules) || formules.length === 0) {
    console.warn("getFormulaData: formules parameter is not a valid array:", formules);
    return { error: "No calculation formulas found for this metric." };
  }

  // Check if environment variable is set
  if (!process.env.REACT_APP_HAYA_IP_ADDRESS) {
    console.error("❌ REACT_APP_HAYA_IP_ADDRESS environment variable is not set");
    return { error: "AI calculation service is not configured. Please contact your administrator." };
  }

  const prompt =
    "Calculate this word problem based on the calculation as well as the data given below. Do provide steps to the calculation as well as to how the result was achieved as well as the final result with units in this particular valid json format: {`result`: <result>, `reasoning`: <steps>}. Strictly give the result and the reasoning in that valid json format only having the keys quoted. Do not write ```json just write the json format only.";
  
  const response = {};
  const errors = [];

  const processFormules = async () => {
    try {
      console.log("🚀 Starting formula processing...");
      console.log("📝 Formulas to process:", formules);
      console.log("📊 Form data:", formData);
      
      await Promise.all(
        formules.map(async (item, index) => {
          try {
            console.log(`🧮 Processing formula ${index + 1}/${formules.length}: ${item?.title}`);
            
            if (!item?.fun) {
              console.warn(`⚠️ Formula ${item?.title} has no function defined`);
              response[item.title] = {
                result: "0",
                reasoning: "Formula function is not defined"
              };
              return;
            }

            const queryData = {
              prompt: prompt + " " + item?.fun + " " + JSON.stringify(formData),
            };
            
            console.log(`📤 Sending request for formula: ${item?.title}`);
            const result = await getPromptData(queryData);
            
            if (!result || !result.response) {
              console.error(`❌ No response received for formula: ${item?.title}`);
              response[item.title] = {
                result: "0",
                reasoning: "No response received from calculation service"
              };
              return;
            }

            console.log(`📥 Raw response for ${item?.title}:`, result.response);

            // Try to parse the JSON response
            let parsedResult;
            try {
              parsedResult = JSON.parse(result.response);
              console.log(`✅ Successfully parsed result for ${item?.title}:`, parsedResult);
            } catch (parseError) {
              console.error(`❌ Failed to parse JSON for ${item?.title}:`, parseError);
              console.log(`📝 Raw response that failed to parse:`, result.response);
              
              // Try to extract meaningful information from the response
              const cleanedResponse = result.response.trim();
              if (cleanedResponse.includes('result') && cleanedResponse.includes('reasoning')) {
                // Try to fix common JSON formatting issues
                try {
                  const fixedResponse = cleanedResponse
                    .replace(/```json/g, '')
                    .replace(/```/g, '')
                    .trim();
                  parsedResult = JSON.parse(fixedResponse);
                  console.log(`🔧 Fixed JSON parsing for ${item?.title}:`, parsedResult);
                } catch (fixError) {
                  console.error(`❌ Failed to fix JSON for ${item?.title}:`, fixError);
                  parsedResult = {
                    result: "0",
                    reasoning: `Calculation completed but result parsing failed. Raw response: ${cleanedResponse.substring(0, 200)}...`
                  };
                }
              } else {
                parsedResult = {
                  result: "0",
                  reasoning: `Calculation completed but result format is invalid. Raw response: ${cleanedResponse.substring(0, 200)}...`
                };
              }
            }

            // Validate the parsed result structure
            if (!parsedResult || typeof parsedResult !== 'object') {
              console.warn(`⚠️ Invalid result structure for ${item?.title}:`, parsedResult);
              response[item.title] = {
                result: "0",
                reasoning: "Invalid calculation result format"
              };
              return;
            }

            // Ensure required fields exist
            if (!parsedResult.result || !parsedResult.reasoning) {
              console.warn(`⚠️ Missing required fields in result for ${item?.title}:`, parsedResult);
              response[item.title] = {
                result: parsedResult.result || "0",
                reasoning: parsedResult.reasoning || "Calculation completed but details are missing"
              };
              return;
            }

            response[item.title] = parsedResult;
            console.log(`✅ Successfully processed formula ${item?.title}`);
            
          } catch (formulaError) {
            console.error(`💥 Error processing formula ${item?.title}:`, formulaError);
            errors.push(`Formula ${item?.title}: ${formulaError.message}`);
            response[item.title] = {
              result: "0",
              reasoning: `Calculation failed: ${formulaError.message}`
            };
          }
        })
      );
    } catch (error) {
      console.error("💥 Critical error in formula processing:", error);
      errors.push(`Processing error: ${error.message}`);
    }
  };

  try {
    await processFormules();
    
    // Log summary of results
    console.log("📊 Calculation summary:", {
      totalFormulas: formules.length,
      successfulResults: Object.keys(response).filter(key => !response[key].error).length,
      errors: errors.length,
      results: response
    });

    // If there were errors, add them to the response
    if (errors.length > 0) {
      response.calculationErrors = errors;
    }

    return response;
  } catch (error) {
    console.error("💥 Fatal error in getFormulaData:", error);
    return { 
      error: `Calculation service failed: ${error.message}`,
      details: "Please check your connection and try again. If the problem persists, contact support."
    };
  }
};
export const functionCaller = async (func, formdata) => {
  const result = await func()(formdata, getAlldata);

  return result;
};

// Delete Table Item Dropdown Menu for Admins Only
export const DeleteTableItem = ({
  userRole,
  deleteHandler,
  item,
  toolTiptext = "Delete Metric",
  DropDownText,
  isDisabled = false,
}) => {
  console.log("DeleteTableItem rendered with:", { userRole, item, isDisabled });
  
  const handleDelete = () => {
    console.log("Delete button clicked directly");
    console.log("User role:", userRole);
    console.log("Is disabled:", isDisabled);
    // Check for both "Admin" and "admin" cases
    if ((userRole === "Admin" || userRole === "admin") && !isDisabled) {
      console.log("Calling delete handler for item:", item);
      deleteHandler(item?.id, item?.type);
    }
  };

  const isAdmin = userRole === "Admin" || userRole === "admin";

  return (
    <div className="flex gap-4 items-center">
      <button 
        className="cursor-pointer p-3 rounded-lg hover:bg-red-50 border-2 border-red-200 hover:border-red-300 transition-all duration-200 bg-white shadow-sm hover:shadow-md relative z-10 pointer-events-auto" 
        style={{ position: 'relative', zIndex: 10, pointerEvents: 'auto' }}
        onClick={(e) => {
          console.log("Delete button onClick triggered");
          console.log("Event target:", e.target);
          console.log("Event currentTarget:", e.currentTarget);
          console.log("Event type:", e.type);
          e.preventDefault();
          e.stopPropagation();
          console.log("Delete button clicked, preventing propagation");
          handleDelete();
        }}
        type="button"
        aria-label="Delete item"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleDelete();
          }
        }}
      >
        <Tooltip size="sm" content={isAdmin ? toolTiptext : "Admin access required"}>
          <div className="flex items-center gap-2">
            <span className="text-sm text-red-600 font-medium">🗑️ Delete</span>
          </div>
        </Tooltip>
      </button>
    </div>
  );
};

// Enhanced DeleteTableItem with unified modal support
export const DeleteTableItemWithModal = ({
  userRole,
  deleteHandler,
  item,
  toolTiptext = "Delete Item",
  setDeleteItemData,
  onOpen,
  isDisabled = false,
}) => {
  const handleDeleteClick = (event) => {
    event.stopPropagation();
    if ((userRole === "Admin" || userRole === "admin") && !isDisabled) {
      setDeleteItemData({ 
        id: item?.id, 
        name: item?.attributes?.Name || item?.attributes?.name || `Item #${item?.id}`,
        type: item?.type 
      });
      onOpen();
    }
  };

  return (
    <div
      className={`flex gap-4 items-center cursor-pointer ${(userRole !== "Admin" && userRole !== "admin") ? "pointer-events-none opacity-50" : ""
        }`}
    >
      <div 
        className="cursor-pointer" 
        onClick={handleDeleteClick}
        role="button"
        tabIndex={0}
        aria-label="Delete item"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleDeleteClick();
          }
        }}
      >
        <Tooltip size="sm" content={toolTiptext}>
          <lord-icon
            src="https://cdn.lordicon.com/wpyrrmcq.json"
            trigger="hover"
            aria-label="Delete item"
            style={{
              width: "20px",
              height: "20px",
            }}
          ></lord-icon>
        </Tooltip>
      </div>
    </div>
  );
};

export const CopyTableItem = ({
  userRole,
  copyHandler,
  item,
  toolTiptext = "Copy Metric",
  DropDownText,
}) => {
  console.log("CopyTableItem rendered with:", { userRole, item });
  
  const handleCopy = () => {
    console.log("Copy button clicked directly");
    console.log("User role:", userRole);
    // Check for both "Admin" and "admin" cases
    if (userRole === "Admin" || userRole === "admin") {
      console.log("Calling copy handler for item:", item);
      copyHandler(item?.id);
    }
  };

  const isAdmin = userRole === "Admin" || userRole === "admin";

  return (
    <div className="flex gap-4 items-center">
      <button 
        className="cursor-pointer p-3 rounded-lg hover:bg-emerald-50 border-2 border-emerald-200 hover:border-emerald-300 transition-all duration-200 bg-white shadow-sm hover:shadow-md relative z-10 pointer-events-auto" 
        style={{ position: 'relative', zIndex: 10, pointerEvents: 'auto' }}
        onClick={(e) => {
          console.log("Copy button onClick triggered");
          console.log("Event target:", e.target);
          console.log("Event currentTarget:", e.currentTarget);
          console.log("Event type:", e.type);
          e.preventDefault();
          e.stopPropagation();
          console.log("Copy button clicked, preventing propagation");
          handleCopy();
        }}
        type="button"
        aria-label="Copy item"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleCopy();
          }
        }}
      >
        <Tooltip size="sm" content={isAdmin ? toolTiptext : "Admin access required"}>
          <div className="flex items-center gap-2">
            <span className="text-sm text-emerald-600 font-medium">📋 Duplicate</span>
          </div>
        </Tooltip>
      </button>
    </div>
  );
};
// Delete Table Item Dropdown Menu for Admins Only
export const EditTableItem = ({
  userRole,
  editHandler,
  item,
  toolTiptext = "Edit Metric",
  DropDownText,
}) => {
  return (
    <div
      className={`flex gap-4 items-center cursor-pointer ${(userRole !== "Admin" && userRole !== "admin") ? "pointer-events-none opacity-50" : ""
        }`}
    >
      <div 
        className="cursor-pointer" 
        onClick={editHandler}
        role="button"
        tabIndex={0}
        aria-label="Edit item"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            editHandler();
          }
        }}
      >
        <Tooltip size="sm" content={toolTiptext}>
          <lord-icon
            src="https://cdn.lordicon.com/wuvorxbv.json"
            trigger="hover"
            stroke="bold"
            aria-label="Edit item"
            style={{
              width: "20px",
              height: "20px",
            }}
          />
        </Tooltip>
      </div>
    </div>
  );
};
// Search Input Component

export const SearchInput = ({ searchText, setSearchText, placeholderText }) => {
  return (
    <Input
      type="text"
      startContent={
        <SearchIcon
          className={`text-large text- pointer-events-none flex-shrink-0 `}
        />
      }
      placeholder={placeholderText}
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      aria-label={placeholderText || "Search input"}
      className="w-full"
      classNames={{
        input: "w-full",
        inputWrapper: "w-full"
      }}
    />
  );
};

// File Table Cell Component
export const FileTableCell = (srcLink) => {
  return (
    <TableCell>
      <lord-icon
        src="https://cdn.lordicon.com/rbbnmpcf.json"
        trigger="hover"
        aria-label="File icon"
        style={{
          width: "20px",
          height: "20px",
        }}
        className="cursor-grab"
      // onClick={(event) => {
      //   event.stopPropagation();
      //   window.open(srcLink);
      // }}
      ></lord-icon>
    </TableCell>
  );
};

export const EmptyTableCell = () => {
  return <TableCell>--</TableCell>;
};

export const GenericTableCell = (value) => {
  return (
    <TableCell>
      {typeof value === "string" ? StringLimit(value, 80) : value}
    </TableCell>
  );
};

export const DeleteTableItemWithOpenModal = (
  id,
  isActive,
  setDeleteMetricId,
  onOpen
) => {
  return (
    <TableCell>
      <div className="flex gap-4 text-center">
        <div
          className={`cursor-pointer ${isActive === false ? "pointer-events-none opacity-50" : ""
            }`}
          onClick={(event) => {
            event.stopPropagation();
            setDeleteMetricId(id);
            onOpen();
          }}
        >
          <Tooltip size="sm" content="Delete Item">
            <lord-icon
              src="https://cdn.lordicon.com/wpyrrmcq.json"
              trigger="hover"
              aria-label="Delete item"
              style={{ width: "20px", height: "20px" }}
            ></lord-icon>
          </Tooltip>
        </div>
      </div>
    </TableCell>
  );
};

// Enhanced delete table item with unified modal support
export const DeleteTableItemWithUnifiedModal = (
  id,
  isActive,
  setDeleteItemId,
  onOpen,
  itemName = "",
  tooltipText = "Delete Item"
) => {
  return (
    <TableCell>
      <div className="flex gap-4 text-center">
        <div
          className={`cursor-pointer ${isActive === false ? "pointer-events-none opacity-50" : ""
            }`}
          onClick={(event) => {
            event.stopPropagation();
            setDeleteItemId({ id, name: itemName });
            onOpen();
          }}
        >
          <Tooltip size="sm" content={tooltipText}>
            <lord-icon
              src="https://cdn.lordicon.com/wpyrrmcq.json"
              trigger="hover"
              
              style={{ width: "20px", height: "20px" }}
            ></lord-icon>
          </Tooltip>
        </div>
      </div>
    </TableCell>
  );
};

export const UserCell = (email, id, url) => {
  const avatarUrl = url ? url : "https://images.unsplash.com/broken";
  return (
    <TableCell>
      <Tooltip content={email || "No User"}>
        <Avatar
          radius="sm"
          // size="sm"
          showFallback
          className="h-7 w-7 cursor-pointer"
          color={Colors[id % numColors]}
          name={email?.toUpperCase().slice(0, 1)}
          src={avatarUrl}
        />
      </Tooltip>
    </TableCell>
  );
};

// Get the User role in current Organization from API
export const getUserRoleFromOrg = async (orgId, userId) => {
  try {
    const orgResponse = await getDataWithFilterUrl(
      "organizations",
      `filters[id][$eqi]=${orgId}`
    );
    const OrgInfo = orgResponse?.data[0];

    if (!OrgInfo) return "Not a User";
    let userRole = "Authenticated";

    const admiList = OrgInfo?.attributes?.admin_list?.data;
    const reviewerList = OrgInfo?.attributes?.reviewer_list?.data;
    const approverList = OrgInfo?.attributes?.approver_list?.data;

    // Check if user is in admin list
    if (admiList && admiList.some(element => element.id === userId)) {
      userRole = "Admin";
    }
    // Check if user is in reviewer list
    else if (reviewerList && reviewerList.some(element => element.id === userId)) {
      userRole = "Reviewer";
    }
    // Check if user is in approver list
    else if (approverList && approverList.some(element => element.id === userId)) {
      userRole = "Approver";
    }

    console.log("🔐 getUserRoleFromOrg:", { orgId, userId, userRole, OrgInfo });
    return userRole;
  } catch (error) {
    console.error("Error in getUserRoleFromOrg:", error);
    return "Authenticated"; // Default fallback
  }
};

// Get the User role in current Organization from Local Storage
export const getUserRoleFromLocal = () => {
  const userData = JSON.parse(sessionStorage.getItem("userData"));
  if (!userData) return "null";
  return userData?.user?.userRole;
};

// Refresh user role from organization and update sessionStorage
export const refreshUserRole = async (orgId, userId) => {
  try {
    const orgRole = await getUserRoleFromOrg(orgId, userId);
    
    // Update sessionStorage with the fresh role
    const userData = JSON.parse(sessionStorage.getItem("userData"));
    if (userData && userData.user) {
      userData.user.userRole = orgRole;
      sessionStorage.setItem("userData", JSON.stringify(userData));
      console.log("🔄 User role refreshed:", orgRole);
    }
    
    return orgRole;
  } catch (error) {
    console.error("Error refreshing user role:", error);
    return null;
  }
};

// Add the given email list to Invitation Collection and send email notification as well for the given Organization
export const addUsersToInvitation = async (list, orgId, UserId) => {
  const result = list.map(async (email) => {
    const uploadData = {
      email: email,
      Status: "Pending",
      organization: [orgId],
      sendBy: [UserId],
    };

    const response = await PublishData({ data: uploadData }, "invitations");
    return response;
  });

  await Promise.all(result);

  const invitations = (list || []).map(async (email) => {
    const payload = {
      to: email,
      subject: "Invitation from Audyt Base.",
      html: "<html><body><p>Hello </p></body></html>",
    };
    const result = await InviteUser(payload);
    return result;
  });

  await Promise.all(invitations);

  return result;
};

export const addUsersToOrganization = async (list, orgId) => {
  const userIdList = [];

  const result = list.map(async (email) => {
    const userResponse = await getDataWithFilterUrl(
      "users",
      `filters[email][$eqi]=${email}`
    );
    if (userResponse[0]?.id) userIdList.push(userResponse[0]?.id);

    return userResponse;
  });

  await Promise.all(result);

  const uploadData = {
    user_lists: {
      connect: [...userIdList],
    },
  };
  const updatedOrgResponse = await UpdateOneData(
    "organizations",
    orgId,
    uploadData
  );

  return updatedOrgResponse;
};

// get user list of current organization members
export const getAllUserOfOrgnization = async () => {
  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;

  const response = await getDataWithFilterUrl(
    "organizations",
    `filters[id][$eqi]=${OrgInfo}`
  );
  let userList = response?.data[0]?.attributes?.user_lists?.data?.map(
    (item) => {
      return {
        id: item.id,
        email: item.attributes?.email,
        username: item.attributes?.username,
      };
    }
  );

  return userList;
};

// get All Reviewer list of current organization members
export const getAllReviewerOfOrgnization = async () => {
  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;

  const response = await getDataWithFilterUrl(
    "organizations",
    `filters[id][$eqi]=${OrgInfo}`
  );
  let userList = response?.data[0]?.attributes?.reviewer_list?.data?.map(
    (item) => {
      return {
        id: item.id,
        email: item.attributes?.email,
        username: item.attributes?.username,
      };
    }
  );

  return userList;
};

// get All Approver list of current organization members
export const getAllApproverOfOrgnization = async () => {
  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;

  const response = await getDataWithFilterUrl(
    "organizations",
    `filters[id][$eqi]=${OrgInfo}`
  );
  let userList = response?.data[0]?.attributes?.approver_list?.data?.map(
    (item) => {
      return {
        id: item.id,
        email: item.attributes?.email,
        username: item.attributes?.username,
      };
    }
  );

  return userList;
};

// get all metrics in which the given user in assigned as Reviewer/Approver based on input

export const getMetricsAssignedToUser = async (
  userId,
  FieldName = "ReviewerToMetric"
) => {
  const userData = await getDataWithFilterUrl(
    "users",
    `filters[id][$eqi]=${userId}`
  );
  return userData[0][FieldName];
};

// // Add Evidence item
// export const addEvidence = async (EvidenceUrl, ReferenceUrl, OrgId) => {

//   const uploadData = {
//     EvidenceUrl,
//     ReferenceUrl,
//     organization: OrgId,
//   };

//   const response = await PublishData({ data: uploadData }, "evidences");

//   return response;
// };

// const duumyMetrics = [
//   {
//     id: 212,
//     attributes: {
//       Name: " Products and Services sold by the Company",
//       Category: "Framework",
//       JsonSchema: {
//         type: "object",
//         title: "Sustainability Item Category",
//         required: [],
//         properties: {
//           Remarks: {
//             type: "string",
//             title: "3. Remarks",
//           },
//           "NIC Code": {
//             type: "integer",
//             title:
//               "1. Number of locations where plants and/or operations/offices of the Company are situated locally",
//           },
//           "Main activity": {
//             type: "string",
//             title:
//               "2. Number of locations where plants and/or operations/offices of the Company are situated internationally",
//           },
//         },
//         description: "",
//       },
//       ClassName: "General Disclosures",
//       Description: "",
//       Links: {},
//       Framework: {
//         id: [],
//       },
//       Frameworks: "BRSR",
//       createdAt: "2024-05-12T12:04:08.741Z",
//       updatedAt: "2024-06-26T11:55:56.287Z",
//       publishedAt: "2024-05-13T06:11:53.886Z",
//       WorkFlow: null,
//       Frequency: null,
//       TagList: null,
//       Organization: {
//         data: null,
//       },
//       AssignedTo: {
//         data: [],
//       },
//       ReviewerTo: {
//         data: [],
//       },
//       ApproverTo: {
//         data: [],
//       },
//     },
//   },
// ];
// const duumyResponses = [
//   {
//     id: 212,
//     attributes: {
//       Metric_Id: "Fuel type",
//       JsonSchema: {
//         Name: "Gas Works Gas",
//         Description: "",
//       },
//       createdAt: "2024-06-09T06:41:20.024Z",
//       updatedAt: "2024-06-09T06:41:20.024Z",
//       publishedAt: "2024-06-09T06:41:20.022Z",
//       Status: null,
//       Comments: null,
//       users_permissions_user: {
//         data: {
//           id: 51,
//           attributes: {
//             username: "sreevatsankomundur@gmail.com",
//             email: "sreevatsankomundur@gmail.com",
//             provider: "local",
//             confirmed: true,
//             blocked: false,
//             FirstName: "Sreevatsan",
//             LastName: "Sridhar",
//             Password2:
//               "VTJGc2RHVmtYMTl0YmpKclVJZWdYaHZ6c2c1MVp3K0VxVldlRGtYTkwvZz0=",
//             createdAt: "2024-05-12T12:38:47.137Z",
//             updatedAt: "2024-06-26T04:58:18.840Z",
//             LastOrg: null,
//             LastOrgId: null,
//             userRole: null,
//           },
//         },
//       },
//       Organization: {
//         data: null,
//       },
//     },
//   },
// ];
export const AddedDefalutMetricsToNewOrg = async (orgId, defaultOrgId) => {
  const filterUrl = "filters[Organization][id][$eqi]=" + defaultOrgId;
  const defalutMetricsReponse = await getDataWithFilterUrl(
    "metrics",
    filterUrl
  );
  const defalutMetrics = defalutMetricsReponse?.data;

  const resultResponses = defalutMetrics?.map(async (item) => {
    const uploadData = {
      ...item?.attributes,
      Organization: {
        connect: [orgId],
      },
    };
    const result = await PublishData({ data: uploadData }, "metrics");
    return result;
  });
  await Promise.all(resultResponses);
};

export const AddedDefalutReponsesToNewOrg = async (
  orgId,
  userId,
  defaultOrgId
) => {
  const filterUrl = "filters[Organization][id][$eqi]=" + defaultOrgId;
  const defalutResResponse = await getDataWithFilterUrl("responses", filterUrl);

  const defalutResResponseList = defalutResResponse?.data;

  const resultResponses = defalutResResponseList?.map(async (item) => {
    const uploadData = item?.attributes;
    uploadData.Organization = {
      connect: [orgId],
    };
    uploadData.users_permissions_user = {
      connect: [userId],
    };
    const result = await PublishData({ data: uploadData }, "responses");
    return result;
  });
  await Promise.all(resultResponses);
};

/*
First we fetch the framework which has a orgName is deafult-
then for each framework we see the corresponding metrics id for new org 
then create a new framwork for new org.
*/

export const AddedDefalutFrameWorkToNewOrg = async (
  newOrgId,
  frameWorkIdList
) => {
  const frameWorkList = [];
  const frameWorkResponse = frameWorkIdList?.map(async (id) => {
    const result = await getDataWithFilterUrl(
      "frameworks",
      `filters[id][$eqi]=${+id}`
    );
    frameWorkList.push(result?.data[0]);
  });
  await Promise.all(frameWorkResponse);

  const newFrameworkList = [];

  const result = frameWorkList?.map(async (item) => {
    const metricIdList = item?.attributes?.ControlMetrics?.id;
    const newMetricIdList = [];

    const metricIdResponses = metricIdList?.map(async (id) => {
      const metricInDefault = await getDataWithFilterUrl(
        "metrics",
        `filters[id][$eqi]=${+id}`
      );
      const metricName =
        metricInDefault?.data[0]?.attributes?.Name?.toLowerCase();
      const metricInNewOrg = await getDataWithFilterUrl(
        "metrics",
        `filters[Organization][id][$eqi]=${newOrgId}&filters[Name][$eqi]=${metricName}`
      );
      newMetricIdList.push(metricInNewOrg?.data[0]?.id);
      return metricInNewOrg;
    });

    await Promise.all(metricIdResponses);
    const newFrameWorkObj = {
      ...item?.attributes,
      ControlMetrics: {
        id: newMetricIdList,
      },
      Organization: {
        connect: [newOrgId],
      },
    };
    newFrameworkList.push(newFrameWorkObj);
    return metricIdResponses;
  });

  await Promise.all(result);

  const newFrameWorkResult = newFrameworkList?.map(async (item) => {
    const result = await PublishData({ data: item }, "frameworks");
    return result;
  });
  await Promise.all(newFrameWorkResult);

  return newFrameWorkResult;
};

export const CreatedNewFinancialToOrg = async (data, orgId) => {
  const uploadData = {
    Name: data?.["Financial Name"],
    StartDate: data?.["Start Date"],
    EndDate: data?.["End Date"],
    Organization: {
      connect: [orgId],
    },
  };
  const result = await PublishData({ data: uploadData }, "financial-years");
  return result;
};

// Updating the Metric Current Coutner during creating the new response.

export const increasedMetricCurrentCount = async (
  metricId,
  createdAt = new Date()
) => {
  const metricResponse = await getDataWithFilterUrl(
    "metrics",
    `filters[id][$eqi]=${+metricId}`
  );
  const metricDetails = metricResponse?.data[0];

  if (!metricDetails) {
    throw new Error("Invalid Metric ID: " + metricId);
  }
  const currentCount = metricDetails?.attributes?.currentCounter;
  const updatedCount = +currentCount + 1;

  const updatedData = {
    currentCounter: updatedCount,
    lastUpdatedReponse: createdAt,
  };

  const result = await UpdateOneData("metrics", metricId, updatedData);
  return result;
};

// Updating the Metric Current Coutner during deleting the new response.

export const decreasedMetricCurrentCount = async (
  metricId,
  createdAt = new Date()
) => {
  const metricResponse = await getDataWithFilterUrl(
    "metrics",
    `filters[id][$eqi]=${+metricId}`
  );
  const metricDetails = metricResponse?.data[0];

  if (!metricDetails) {
    throw new Error("Invalid Metric ID: " + metricId);
  }
  const currentCount = metricDetails?.attributes?.currentCounter;
  const updatedCount = +currentCount - 1;

  const updatedData = {
    currentCounter: updatedCount,
    lastUpdatedReponse: createdAt,
  };
  const result = await UpdateOneData("metrics", metricId, updatedData);
  return result;
};

// Updating the Metric Current Coutner during deleting the new response.

export const updatedMetriclastUpdateResponse = async (
  metricId,
  createdAt = new Date()
) => {
  const metricResponse = await getDataWithFilterUrl(
    "metrics",
    `filters[id][$eqi]=${+metricId}`
  );
  const metricDetails = metricResponse?.data[0];

  if (!metricDetails) {
    throw new Error("Invalid Metric ID: " + metricId);
  }

  const updatedData = {
    lastUpdatedReponse: createdAt,
  };

  const result = await UpdateOneData("metrics", metricId, updatedData);
  return result;
};

// Sync metric counter with actual response count from database
export const syncMetricCurrentCount = async (metricId, orgId, metricName) => {
  try {
    // Get actual count from responses table
    const responseUrl = `filters[Organization][id][$eqi]=${orgId}&filters[Metric_Id][$eqi]=${metricName}`;
    const responseData = await getDataWithFilterUrl("responses", responseUrl);
    const actualCount = responseData?.data?.length || 0;
    
    // Get current metric details
    const metricResponse = await getDataWithFilterUrl(
      "metrics",
      `filters[id][$eqi]=${+metricId}`
    );
    const metricDetails = metricResponse?.data[0];
    
    if (!metricDetails) {
      throw new Error("Invalid Metric ID: " + metricId);
    }
    
    const currentStoredCount = metricDetails?.attributes?.currentCounter || 0;
    
    // Only update if counts don't match
    if (currentStoredCount !== actualCount) {
      console.log(`Syncing metric ${metricId}: stored count ${currentStoredCount} -> actual count ${actualCount}`);
      
      const updatedData = {
        currentCounter: actualCount,
        lastUpdatedReponse: new Date().toISOString(),
      };
      
      const result = await UpdateOneData("metrics", metricId, updatedData);
      return result;
    }
    
    return metricDetails;
  } catch (error) {
    console.error("Error syncing metric count:", error);
    throw error;
  }
};

// Get Status,CurrentCount,RequiredCount of Metric Responses for an organization
export const getMetricStatus = async (metricID) => {
  const url = `filters[id][$eqi]=${metricID}`;

  const result = await getFilterDataWithPagination("metrics", url);

  const lastUpdatedAt = result?.data[0]?.attributes?.lastUpdatedReponse;
  const currCount = result?.data[0]?.attributes?.currentCounter;
  let requiredCount = result?.data[0]?.attributes?.currentCounter;

  const status = currCount >= requiredCount ? "Completed" : "Incompleted";

  return {
    Status: status,
    RequiredCount: requiredCount,
    CurrentCount: currCount,
    lastUpdatedAt: lastUpdatedAt,
  };
};

export const hasCommonElement = (arr1, arr2) => {
  const set1 = new Set(arr1);

  for (let element of arr2) {
    if (set1.has(+element)) {
      return true; // Common element found
    }
  }

  return false; // No common elements
};

export const getSchemaForOrg = async (responseData, orgId) => {
  if (!orgId || !responseData) return [];

  const frameworkUrl = `filters[organizations][id][$eqi]=${+orgId}`;
  let frameworkResponse = await getDataWithFilterUrl(
    "frameworks",
    frameworkUrl
  );

  frameworkResponse = frameworkResponse?.data;

  const frameWorkIdList = frameworkResponse?.map((item) => {
    return item?.id;
  });

  let responseArr = [];

  // First, add fields that have matching frameworks
  Object.keys(responseData)?.forEach((key) => {
    const fieldFramework = responseData[key]?.framework || [];
    if (hasCommonElement(frameWorkIdList, fieldFramework)) {
      responseArr.push([key, responseData[key]]);
    }
  });

  // If no framework-matched fields found, add all fields as fallback
  if (responseArr.length === 0) {
    console.log("No framework-matched fields found, using all fields as fallback");
    Object.keys(responseData)?.forEach((key) => {
      responseArr.push([key, responseData[key]]);
    });
  }

  responseArr?.sort((a, b) => {
    return a[1]?.id - b[1]?.id;
  });

  return responseArr || [];
};
export const getSchemaForFramework = async (
  responseData,
  orgId,
  frameworkName
) => {
  const frameworkUrl = `filters[Name][$eqi]=${frameworkName}`;
  let frameworkResponse = await getDataWithFilterUrl(
    "frameworks",
    frameworkUrl
  );

  frameworkResponse = frameworkResponse?.data;

  const frameWorkIdList = frameworkResponse?.map((item) => {
    return item?.id;
  });
  let responseArr = [];

  Object.keys(responseData)?.forEach((key) => {
    const fieldFramework = responseData[key]?.framework || [];

    if (hasCommonElement(frameWorkIdList, fieldFramework)) {
      responseArr.push([key, responseData[key]]);
    }
  });

  responseArr?.sort((a, b) => {
    return a[1]?.id - b[1]?.id;
  });

  return responseArr || [];
};
export const getFrameworkUrl = (list = []) => {
  let url = "";
  list?.forEach((id, index) => {
    url = url + `&filters[$or][${index}][Framework][$containsi]=${id}`;
  });
  return url;
};

export const encryptedText = (text) => {
  const secretKey = `${process.env.REACT_APP_ENCRYPT_SECRET_KEY}`;
  const inputString = text;
  const encrypted = CryptoJS.AES.encrypt(inputString, secretKey);
  const AESHash = encrypted.toString();
  const url = btoa(AESHash);
  return url;
};

export const decryptedText = (encryptedText) => {
  try {
    // Validate input
    if (!encryptedText || typeof encryptedText !== 'string' || encryptedText.trim() === '') {
      throw new Error('Invalid encrypted text: empty or null value');
    }
    
    const secretKey = `${process.env.REACT_APP_ENCRYPT_SECRET_KEY}`;
    
    // Check if the string is valid base64 before calling atob
    if (!/^[A-Za-z0-9+/]*={0,2}$/.test(encryptedText)) {
      throw new Error('Invalid encrypted text: not a valid base64 string');
    }
    
    const inputString = atob(encryptedText).toString();

    const text = CryptoJS.AES.decrypt(inputString, secretKey).toString(
      CryptoJS.enc.Utf8
    );

    return text;
  } catch (error) {
    console.error('Decryption error:', error);
    throw error;
  }
};

export const getFieldMoreInfoContent = (item, frameworkName = null) => {
  const data = {};

  if (Object.prototype.toString.call(item) !== "[object Object]") return data;

  Object.keys(item?.["guidance"] || {})?.forEach((element) => {
    if (
      frameworkName === null ||
      element?.toLowerCase() === frameworkName?.toLowerCase()
    ) {
      if (Object.prototype.toString.call(data[element]) === "[object Object]") {
        data[element] = {
          ...data[element],
          guidance: item["guidance"][element],
        };
      } else {
        data[element] = { guidance: item["guidance"][element] };
      }
    }
  });

  Object.keys(item?.["overview"] || {}).forEach((element) => {
    if (
      frameworkName === null ||
      element?.toLowerCase() === frameworkName?.toLowerCase()
    ) {
      if (Object.prototype.toString.call(data[element]) === "[object Object]") {
        data[element] = {
          ...data[element],
          overview: item["overview"][element],
        };
      } else {
        data[element] = { guidence: item["overview"][element] };
      }
    }
  });

  Object.keys(item?.["reference"] || {}).forEach((element) => {
    if (
      frameworkName === null ||
      element?.toLowerCase() === frameworkName?.toLowerCase()
    ) {
      if (Object.prototype.toString.call(data[element]) === "[object Object]") {
        data[element] = {
          ...data[element],
          reference: item["reference"][element],
        };
      } else {
        data[element] = { guidence: item["reference"][element] };
      }
    }
  });

  return data;
};

export const generateOtp = () => {
  const otp = Math.floor(100000 + Math.random() * 900000);

  return otp.toString();
};

// const getImageForDomain = (domainName) => {
//   switch (domainName.toLowerCase()) {
//     case "environment":
//       return environmentImage;
//     case "governance":
//       return governanceImage;
//     case "social":
//       return socialImage;
//     case "survey":
//       return surveyImage;
//     case "general":
//       return generalImage;
//     default:
//       return generalImage; // Fallback image
//   }
// };
export const getFieldInfoListFromGetJSonSchema = (List) => {
  console.log("getFieldInfoListFromGetJSonSchema - Input List:", List);
  const response = [];
  const fieldOrder = List?.fieldOrder || []; // Get saved field order
  const properties = List?.properties || {};
  console.log("getFieldInfoListFromGetJSonSchema - fieldOrder:", fieldOrder);
  console.log("getFieldInfoListFromGetJSonSchema - properties:", properties);
  
  // Helper function to process a field
  const processField = (key) => {
    const fieldData = properties[key];
    if (!fieldData) return;
    

    
    if (fieldData.type === "relation") {
      response.push({
        id: fieldData.id,
        Title: key,
        Required: fieldData.required,
        Type: fieldData.type,
        name: FieldList[fieldData.type]?.title || "Relation",
        relatedTo: fieldData.relatedto,
        metricName: fieldData.metricname,
        isMultiSelect: fieldData.ismultiselect,
        framework: fieldData.framework,
        displayOrder: fieldData.displayOrder,
        assignedGroup: fieldData.assignedGroup,
      });
    } else if (fieldData.type === "dropdown") {
      response.push({
        id: fieldData.id,
        Title: key,
        Required: fieldData.required,
        Type: fieldData.type,
        name: FieldList[fieldData.type]?.title || "Dropdown",
        values: fieldData.values,
        isMultiSelect: fieldData.ismultiselect,
        framework: fieldData.framework,
        displayOrder: fieldData.displayOrder,
        assignedGroup: fieldData.assignedGroup,
      });
    } else if (fieldData.type === "integer") {
      response.push({
        id: fieldData.id,
        Title: key,
        Required: fieldData.required,
        Type: fieldData.type,
        name: FieldList[fieldData.type]?.title || "Integer",
        min_val: fieldData.min_val,
        max_val: fieldData.max_val,
        framework: fieldData.framework,
        displayOrder: fieldData.displayOrder,
        assignedGroup: fieldData.assignedGroup,
      });
    } else if (fieldData.type === "slider") {
      response.push({
        id: fieldData.id,
        Title: key,
        Required: fieldData.required,
        Type: fieldData.type,
        name: FieldList[fieldData.type]?.title || "Slider",
        min_val: fieldData.min_val,
        max_val: fieldData.max_val,
        step_val: fieldData.step_val,
        framework: fieldData.framework,
        displayOrder: fieldData.displayOrder,
        assignedGroup: fieldData.assignedGroup, 
      });
    } else if (fieldData.type === "text" || fieldData.type === "textarea") {
      response.push({
        id: fieldData.id,
        Title: key,
        Required: fieldData.required,
        Type: fieldData.type,
        name: FieldList[fieldData.type]?.title || fieldData.type,
        char_limit: fieldData?.char_limit || "",
        framework: fieldData.framework,
        displayOrder: fieldData.displayOrder,
        assignedGroup: fieldData.assignedGroup,

      });
    } else if (fieldData.type === "file") {
      response.push({
        id: fieldData.id,
        Title: key,
        Required: fieldData.required,
        Type: fieldData.type,
        name: FieldList[fieldData.type]?.title || "File",
        acceptFieldType: fieldData?.acceptfieldtype || [],
        framework: fieldData.framework,
        displayOrder: fieldData.displayOrder,
        assignedGroup: fieldData.assignedGroup,

      });
    } else if (fieldData.type === "formula") {
      response.push({
        id: fieldData.id,
        Title: key,
        Required: fieldData.required,
        Type: fieldData.type,
        name: "Formula Calculation",
        fun: fieldData.fun,
        framework: fieldData.framework,
        displayOrder: fieldData.displayOrder,
        assignedGroup: fieldData.assignedGroup,

      });
    } else if (fieldData.type === "conditional") {
      response.push({
        id: fieldData.id,
        Title: key,
        Required: fieldData.required,
        Type: fieldData.type,
        name: FieldList[fieldData.type]?.title || "Conditional Field",
        dependentField: fieldData.dependentField,
        triggerValue: fieldData.triggerValue,
        dependentFieldType: fieldData.dependentFieldType,
        dependentFieldConfig: fieldData.dependentFieldConfig,
        framework: fieldData.framework,
        displayOrder: fieldData.displayOrder,
        assignedGroup: fieldData.assignedGroup,

      });
    } else if (fieldData.type === "subheading") {
      response.push({
        id: fieldData.id,
        Title: key,
        Required: fieldData.required,
        Type: fieldData.type,
        name: FieldList[fieldData.type]?.title || "Subheading",
        headingLevel: fieldData.headingLevel,
        subheadingDescription: fieldData.subheadingDescription,
        framework: fieldData.framework,
        displayOrder: fieldData.displayOrder,

      });
    } else {
      response.push({
        id: fieldData.id,
        Title: key,
        Required: fieldData.required,
        Type: fieldData.type,
        name: FieldList[fieldData.type]?.title || fieldData.type,
        framework: fieldData.framework,
        displayOrder: fieldData.displayOrder,
        assignedGroup: fieldData.assignedGroup,

      });
    }
  };

  // Process fields in the saved order if available
  if (fieldOrder.length > 0) {
    fieldOrder.forEach(key => {
      processField(key);
    });
    
    // Handle any fields that might not be in the order list (backwards compatibility)
    Object.keys(properties).forEach(key => {
      if (!fieldOrder.includes(key)) {
        processField(key);
      }
    });
  } else {
    // Fallback: sort by displayOrder or id for backwards compatibility
    const fieldKeys = Object.keys(properties);
    const sortedKeys = fieldKeys.sort((a, b) => {
      const aOrder = properties[a].displayOrder;
      const bOrder = properties[b].displayOrder;
      if (aOrder !== undefined && bOrder !== undefined) {
        return aOrder - bOrder;
      }
      // Fallback to id-based sorting for old data
      return properties[a].id - properties[b].id;
    });
    
    sortedKeys.forEach(key => {
      processField(key);
    });
  }

  console.log("getFieldInfoListFromGetJSonSchema - Final response:", response);
  return response;
};

export const getLinkInfoListFromJson = (list) => {
  const response = [];

  list?.forEach((item) => {
    response.push({
      id: item.id,
      Title: item.name,
      link: item.link,
      Type: "link",
      name: "link",
    });
  });
  return response;
};

// Function to clean up existing forms by removing guidance, overview, and reference fields
export const cleanUpExistingForm = (jsonSchema) => {
  if (!jsonSchema || !jsonSchema.properties) {
    return jsonSchema;
  }

  const cleanedProperties = {};
  
  Object.keys(jsonSchema.properties).forEach(fieldName => {
    const field = jsonSchema.properties[fieldName];
    const cleanedField = { ...field };
    
    // Remove guidance, overview, and reference fields
    delete cleanedField.guidance;
    delete cleanedField.overview;
    delete cleanedField.reference;
    
    cleanedProperties[fieldName] = cleanedField;
  });

  return {
    ...jsonSchema,
    properties: cleanedProperties
  };
};

const flattenJSON = (jsonData, fieldsList) => {
  const flatData = jsonData.map((item) => {
    const { id, attributes } = item;
    const {
      Metric_Id,
      Status,
      JsonSchema,
      users_permissions_user,
      Organization,
    } = attributes;

    const customizeField = {};
    fieldsList.forEach((field) => {
      customizeField[field] = JsonSchema?.[field];
    });

    return {
      id,
      Metric_Id,
      Status,
      ...customizeField, // Spread out the nested JsonSchema object
      username: users_permissions_user?.data?.attributes?.username,
      organization: Organization?.data?.attributes?.Name,
    };
  });
  return flatData;
};

export const exportDownloadHandler = (metricName, type, data, fieldsList) => {
  const fileName = metricName?.toLowerCase().replaceAll(" ", "_");
  const flattenData = flattenJSON(data, fieldsList);

  const downloadJSON = () => {
    const file = new Blob([JSON.stringify(flattenData, null, 2)], {
      type: "application/json",
    });
    saveAs(file, `${fileName}.json`);
  };

  const downloadCSV = () => {
    const csv = Papa.unparse(flattenData);
    const file = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(file, `${fileName}.csv`);
  };

  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(flattenData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(file, `${fileName}.xlsx`);
  };

  if (type === "JSON") {
    downloadJSON();
  } else if (type === "CSV") {
    downloadCSV();
  } else if (type === "Excel") {
    downloadExcel();
  }
};
export const ExportDataComponent = ({ data, metricName }) => {
  const fileName = metricName?.toLowerCase().replaceAll(" ", "_");

  const downloadJSON = () => {
    const file = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    saveAs(file, `${fileName}.json`);
  };

  const downloadCSV = () => {
    const flattenData = flattenJSON(data);
    const csv = Papa.unparse(flattenData);
    const file = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(file, `${fileName}.csv`);
  };

  const downloadExcel = () => {
    const flattenData = flattenJSON(data);
    const worksheet = XLSX.utils.json_to_sheet(flattenData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(file, `${fileName}.xlsx`);
  };

  const downloadHandler = (event) => {
    const value = Array.from(event)[0];
    if (value === "json") {
      downloadJSON();
    }
    if (value === "csv") {
      downloadCSV();
    }
    if (value === "excel") {
      downloadExcel();
    }
  };

  return (
    <Dropdown className="w-max">
      <DropdownTrigger>
        <Button size="sm" color="default" variant="light" className="w-max">
          Export
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Multiple selection"
        variant="flat"
        selectionMode="single"
        disallowEmptySelection
        onSelectionChange={downloadHandler}
        className="custom-dropdown-menu"
      >
        <DropdownItem
          key={"json"}
          startContent={<Image width={20} src={DownloadIcon} />}
        >
          <span className="text-xs">Export as JSON</span>
        </DropdownItem>
        <DropdownItem
          key={"excel"}
          startContent={<Image width={20} src={DownloadIcon} />}
        >
          <span className="text-xs">Export as Excel</span>
        </DropdownItem>
        <DropdownItem
          key={"csv"}
          startContent={<Image width={20} src={DownloadIcon} />}
        >
          <span className="text-xs">Export as CSV</span>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export function getRandomHexColor() {
  const randomColor = Math.floor(Math.random() * 16777215).toString(16);

  return `#${randomColor.padStart(6, "0")}`;
}

/*
-----------------------------
-----------------------------
---------LLM Helper.---------
-----------------------------
-----------------------------
*/

export const prompt = (query) => {
  return {
    prompt:
      "What is the subject of this query: {query}. Your output should only be 3 words",
    variables: [{ variable: "query", value: query }],
  };
};

// Helper function to format dates without external libraries
export const getDateLabel = (timestamp) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  // Check if the date is today
  if (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  ) {
    return "Today";
  }

  // Check if the date is yesterday
  if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    return "Yesterday";
  }

  // Check if the date is within the last 7 days (but not today or yesterday)
  if (date >= sevenDaysAgo) {
    return "Previous 7 days";
  }

  // Check if the date is within the last 30 days (but not in the last 7 days)
  if (date >= thirtyDaysAgo) {
    return "Previous 30 days";
  }

  // For older dates, group by "Month-YYYY" (e.g., "October-2024")
  return date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

// STRING SPLIT FUNCTION
export const renderBoldText = (text) => {
  const boldRegex = /\*\*(.*?)\*\*/g;
  return text.split(boldRegex).map((part, index) => {
    if (index % 2 === 0) {
      return <span key={index}>{part}</span>;
    } else {
      return <strong key={index}>{part}</strong>;
    }
  });
};

export function timeAgo(timestamp) {
  const now = new Date();
  const timeDiff = now - new Date(timestamp);
  const seconds = Math.floor(timeDiff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) return `${seconds} sec ago`;
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours} hr ago`;
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
  if (weeks < 4) return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
  if (years < 2) return `${years} year ago`;

  // If more than 2 years, return formatted date (DD-MM-YYYY)
  const date = new Date(timestamp);
  return `${date.getDate().toString().padStart(2, "0")}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${date.getFullYear()}`;
}
